/**
 * "Baixar exame" no contexto da consulta (E91.F2, GUS-51 RF05/CA05/RN01).
 *
 * A preliminar e a mesa do perito não ofereciam a saída do exame (UXD-05); ela
 * existia só no portal, no fim da página. Este controle reutiliza a MESMA saída
 * (pela porta do perito, E91.F3.T05) e a MESMA condição — o gate
 * `exame_entregue` do servidor — e diz o motivo quando
 * não está disponível, em vez de um botão morto. Não é registrar conclusão nem
 * emitir laudo: é abrir o documento que já existe.
 *
 * E118.F1 (GUS-127, decisão do owner 28/09): as leituras propostas sem confirmação nem
 * corroboração ficam evidentes aqui, com o nome de cada imagem — o botão segue ativo (sem trava).
 * E119.F2 (GUS-132, Z-04): o caso sem banco também — o requerente e a identificação do credor
 * saem sem ele (`bancoDoCaso === null`); `undefined` é "não informado pelo servidor" e não avisa.
 * E124.F1 (GUS-134): e a análise fotográfica automática ainda em andamento — o documento baixado
 * agora sai sem a conferência facial; o aviso some quando ela termina.
 *
 * E205.F2 (ADR-0147 §2): "Baixar exame" é um item do botão "Exame" da barra da aba Exame
 * (`MenuDoExame`); aqui ficam o download (`useBaixarExame`), o motivo de não sair
 * (`motivoDoExame`) e os avisos da emissão, que a barra mostra abaixo dela (`AvisosDaEmissao`).
 * E205.F3 (ADR-0147 §5): a leitura proposta sem conferência (E118.F1) é pendência do servidor, na
 * aba Pendências; o aviso dela saiu daqui.
 */
import { useCallback, useState } from 'react';

import { CodigoDoArquivoBaixado } from '@/components/CodigoDoArquivoBaixado';
import { ptBR } from '@/i18n/pt-BR';
import { useApiClient } from '@/lib/api';
import {
  baixarExame,
  classifySaidaDoExameError,
  type DisponibilidadeDoExame,
  type MotivoIndisponivel,
  type SaidaDoExameErrorReason,
} from '@/lib/exame_eletronico_saida';
import { salvarArquivoBaixado } from '@/lib/http_download';

const t = ptBR.exameEletronico.baixarExame;

const MOTIVO: Record<MotivoIndisponivel, string> = {
  sem_documentos: t.semDocumentos,
  pendente_de_verificacao: t.pendenteDeVerificacao,
  em_processamento: t.emProcessamento,
};

const ERRO: Record<SaidaDoExameErrorReason, string> = {
  nao_disponivel: t.erroNaoDisponivel,
  regua_anterior: t.erroReguaAnterior,
  leitura_em_conferencia: t.erroLeituraEmConferencia,
  sem_perfil_normativo: t.erroSemPerfil,
  not_found: t.erroGeneric,
  forbidden: t.erroGeneric,
  network: t.erroNetwork,
  unknown: t.erroGeneric,
};

/** Por que o exame ainda não sai; `null` quando sai. */
export function motivoDoExame(disponibilidade: DisponibilidadeDoExame): string | null {
  return disponibilidade.disponivel ? null : MOTIVO[disponibilidade.motivo];
}

export interface DownloadDoExame {
  baixar: () => void;
  baixando: boolean;
  erro: string | null;
  codigo: string | null;
}

/** Baixa o exame pela saída de sempre (a porta do perito) e guarda o código do arquivo baixado. */
export function useBaixarExame(caseId: string): DownloadDoExame {
  const client = useApiClient();
  const [baixando, setBaixando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [codigo, setCodigo] = useState<string | null>(null);

  const baixar = useCallback((): void => {
    if (baixando) return;
    void (async () => {
      setBaixando(true);
      setErro(null);
      try {
        const arquivo = await baixarExame(client, caseId);
        setCodigo(arquivo.codigoVerificacao ?? null);
        salvarArquivoBaixado(arquivo);
      } catch (err) {
        const recusa = classifySaidaDoExameError(err);
        setErro(ERRO[recusa.reason].replace('{pecas}', recusa.pendentes.join(', ')));
      } finally {
        setBaixando(false);
      }
    })();
  }, [baixando, client, caseId]);
  return { baixar, baixando, erro, codigo };
}

export interface AvisosDaEmissaoProps {
  /** E119.F2: o nome do banco do caso; `null` = caso sem banco (avisa); `undefined` = não sabe. */
  bancoDoCaso?: string | null | undefined;
  /** E124.F1: a análise fotográfica automática ainda roda (avisa, sem travar). */
  analiseFotograficaEmAndamento?: boolean;
  download: DownloadDoExame;
}

const AVISO = 'text-caption text-warning-700';

export function AvisosDaEmissao({
  bancoDoCaso,
  analiseFotograficaEmAndamento = false,
  download,
}: AvisosDaEmissaoProps): JSX.Element {
  return (
    <div className="space-y-sc-1" data-testid="avisos-da-emissao">
      {bancoDoCaso === null ? (
        <p role="status" className={AVISO} data-testid="baixar-exame-banco-ausente">
          {t.bancoDoCasoAusente}
        </p>
      ) : null}
      {analiseFotograficaEmAndamento ? (
        <p role="status" className={AVISO} data-testid="baixar-exame-fotografica-em-andamento">
          {t.analiseFotograficaEmAndamento}
        </p>
      ) : null}
      {download.erro !== null ? (
        <p role="alert" className="text-caption text-danger-700" data-testid="baixar-exame-erro">
          {download.erro}
        </p>
      ) : null}
      <CodigoDoArquivoBaixado codigo={download.codigo} />
    </div>
  );
}
